export interface ScrollRafRef {
  current: number | null;
}

export interface StickyElementScrollConfig {
  maxAttempts?: number;
  stalledFrames?: number;
  resyncFrames?: number;
  farDistancePx?: number;
  smoothFinishWindowPx?: number;
  preJumpMinDeltaPx?: number;
  epsilonPx?: number;
}

interface StickyElementScrollContext {
  scrollContainer: HTMLElement;
  targetElement: HTMLElement;
  epsilonPx: number;
}

interface StartStickyElementScrollOptions {
  rafIdRef: ScrollRafRef;
  scrollContainer: HTMLElement;
  targetElement: HTMLElement;
  scrollIntoViewOptions: ScrollIntoViewOptions;
  config?: StickyElementScrollConfig;
  isTargetReached?: (context: StickyElementScrollContext) => boolean;
  onFinish?: () => void;
}

const DEFAULT_CONFIG: Required<StickyElementScrollConfig> = {
  maxAttempts: 120,
  stalledFrames: 8,
  resyncFrames: 24,
  farDistancePx: 2400,
  smoothFinishWindowPx: 800,
  preJumpMinDeltaPx: 120,
  epsilonPx: 1,
};

export function cancelStickyElementScroll(rafIdRef: ScrollRafRef) {
  if (rafIdRef.current !== null) {
    cancelAnimationFrame(rafIdRef.current);
    rafIdRef.current = null;
  }
}

export function isElementFullyVisibleWithinContainer(
  scrollContainer: HTMLElement,
  targetElement: HTMLElement,
  epsilonPx = DEFAULT_CONFIG.epsilonPx
) {
  const targetRect = targetElement.getBoundingClientRect();
  const containerRect = scrollContainer.getBoundingClientRect();
  return (
    targetRect.top >= containerRect.top - epsilonPx &&
    targetRect.bottom <= containerRect.bottom + epsilonPx
  );
}

export function getElementScrollTopWithinContainer(
  scrollContainer: HTMLElement,
  targetElement: HTMLElement
) {
  return (
    targetElement.getBoundingClientRect().top -
    scrollContainer.getBoundingClientRect().top +
    scrollContainer.scrollTop
  );
}

export function startStickyElementScroll({
  rafIdRef,
  scrollContainer,
  targetElement,
  scrollIntoViewOptions,
  config,
  isTargetReached,
  onFinish,
}: StartStickyElementScrollOptions) {
  const resolvedConfig: Required<StickyElementScrollConfig> = {
    ...DEFAULT_CONFIG,
    ...config,
  };
  const resolvedBehavior = scrollIntoViewOptions.behavior ?? 'auto';

  const finish = () => {
    rafIdRef.current = null;
    onFinish?.();
  };

  const targetReached =
    isTargetReached ??
    (({
      scrollContainer: currentScrollContainer,
      targetElement: currentTargetElement,
      epsilonPx,
    }: StickyElementScrollContext) => {
      const targetTop = getElementScrollTopWithinContainer(
        currentScrollContainer,
        currentTargetElement
      );
      const isNearTargetTop =
        Math.abs(currentScrollContainer.scrollTop - targetTop) <= epsilonPx;
      return (
        isNearTargetTop ||
        isElementFullyVisibleWithinContainer(
          currentScrollContainer,
          currentTargetElement,
          epsilonPx
        )
      );
    });

  const applyScroll = () => {
    targetElement.scrollIntoView(scrollIntoViewOptions);
  };

  cancelStickyElementScroll(rafIdRef);

  if (resolvedBehavior === 'smooth') {
    const targetTop = getElementScrollTopWithinContainer(
      scrollContainer,
      targetElement
    );
    const distance = Math.abs(scrollContainer.scrollTop - targetTop);
    if (distance > resolvedConfig.farDistancePx) {
      const direction = targetTop > scrollContainer.scrollTop ? 1 : -1;
      const maxScrollTop = Math.max(
        0,
        scrollContainer.scrollHeight - scrollContainer.clientHeight
      );
      const preJumpTop = clamp(
        targetTop - direction * resolvedConfig.smoothFinishWindowPx,
        0,
        maxScrollTop
      );
      if (
        Math.abs(preJumpTop - scrollContainer.scrollTop) >
        resolvedConfig.preJumpMinDeltaPx
      ) {
        scrollContainer.scrollTo({
          top: preJumpTop,
          behavior: 'auto',
        });
      }
    }
  }

  applyScroll();

  let attempts = 0;
  let stalledFrames = 0;
  let lastScrollTop = scrollContainer.scrollTop;

  const tick = () => {
    attempts += 1;

    if (
      targetReached({
        scrollContainer,
        targetElement,
        epsilonPx: resolvedConfig.epsilonPx,
      })
    ) {
      finish();
      return;
    }

    if (attempts > resolvedConfig.maxAttempts) {
      finish();
      return;
    }

    const currentScrollTop = scrollContainer.scrollTop;
    if (
      Math.abs(currentScrollTop - lastScrollTop) <= resolvedConfig.epsilonPx
    ) {
      stalledFrames += 1;
    } else {
      stalledFrames = 0;
    }
    lastScrollTop = currentScrollTop;

    if (
      resolvedBehavior !== 'smooth' ||
      stalledFrames >= resolvedConfig.stalledFrames ||
      attempts % resolvedConfig.resyncFrames === 0
    ) {
      applyScroll();
      stalledFrames = 0;
    }

    rafIdRef.current = requestAnimationFrame(tick);
  };

  rafIdRef.current = requestAnimationFrame(tick);
}

function clamp(value: number, min: number, max: number) {
  return Math.max(min, Math.min(value, max));
}
